/**
 * API e stato server del ripescaggio settimanale dei candidati scaduti (F-184).
 *
 * ⚠️ I tetti di esposizione sono STRINGHE decimali (ADR-0006).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { AssetDomain } from '@/lib/asset-domain';
import { wishlistKeys } from './api';

export type RevivalSettings = {
  enabled: boolean;
  /** ISO: 1 = lunedì, 7 = domenica. */
  weekday: number;
  runAtLocalTime: string;
  timezone: string;
};

export type RevivalTarget = {
  portfolioId: string;
  portfolioName: string;
  baseCurrency: string;
  maxExposureAmount: string | null;
  maxPortfolioPercentage: string | null;
};

export type RevivalPick = {
  status: 'revived' | 'skipped' | 'discarded';
  wishlistItemId: string;
  instrumentId: string | null;
  name: string;
  symbol: string | null;
  domain: AssetDomain | null;
  confidence: number;
  reason: string;
  note: string | null;
  newWishlistItemId: string | null;
  portfolioName: string | null;
  expiresAt: string | null;
};

export type RevivalRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export type RevivalRun = {
  id: string;
  trigger: 'scheduled' | 'manual';
  status: RevivalRunStatus;
  slotDate: string | null;
  agentRunId: string | null;
  candidatesCount: number;
  candidates: Array<{ wishlistItemId: string; name: string; symbol: string | null; domain: AssetDomain; expiredAt: string }>;
  picks: RevivalPick[];
  summary: string | null;
  emptyReason: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
};

export type RevivalOverview = {
  settings: RevivalSettings;
  nextRunAt: string | null;
  agentRuntimeEnabled: boolean;
  lookbackDays: number;
  maxPicks: number;
  defaultExpiryDays: number;
  targets: Record<AssetDomain, RevivalTarget | null>;
  eligibleCount: number;
  withoutPortfolio: Record<AssetDomain, number>;
  runs: RevivalRun[];
};

export const revivalKeys = { overview: ['wishlist-revival'] as const };

export function isInFlight(run: { status?: RevivalRunStatus | undefined } | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

export function useRevivalOverview() {
  return useQuery({
    queryKey: revivalKeys.overview,
    queryFn: ({ signal }) => api.get<RevivalOverview>('/wishlist-revival', signal),
    // Mentre uno studio è in coda o in corso la card si aggiorna da sola: dura minuti.
    refetchInterval: (query) => (isInFlight(query.state.data?.runs[0]) ? 10_000 : false),
  });
}

export function useUpdateRevivalSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<RevivalSettings>) => api.put<RevivalOverview>('/wishlist-revival/settings', input),
    onSuccess: (overview) => queryClient.setQueryData(revivalKeys.overview, overview),
  });
}

export function useRunRevivalNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ runId: string }>('/wishlist-revival/runs'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: revivalKeys.overview });
      void queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
    },
  });
}
